import { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import { noop } from 'lodash';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Loader, Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';

import { FaceVerificationMoalProps } from './types';
import { modalHeaderRenderer } from './util';

export const modalStyles = css`
  width: 38.2rem;
  max-height: 68rem;
  min-height: 52rem;
  min-width: 25rem;
  &.full-height {
    min-height: 68rem;
  }

  @media (max-height: 700px) {
    max-height: 90%;
    &.full-height {
      min-height: 90%;
    }
  }
`;

const landscapeModalStyles = css`
  width: 38.2rem;
  max-height: 68rem;
  min-height: 52rem;
  min-width: 25rem;
  &.full-height {
    min-height: 68rem;
  }

  @media (max-height: 700px) {
    max-height: 90%;
    &.full-height {
      min-height: 90%;
    }
  }

  transform: rotate(270deg) !important;
`;

function FaceVerificationModal({
  videoRef,
  isCameraReady,
  captureImageFromVideo,
  verifyImage,
}: FaceVerificationMoalProps) {
  const { isLandScapeMode } = useScreen();
  const { t } = useTranslation(['ExternalCamera']);
  const [shouldPause, setShouldPause] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  useEffect(() => {
    if (videoRef.current) {
      if (shouldPause) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [shouldPause]);
  const verifyFace = () => {
    setShouldPause(!shouldPause);
    setIsVerifying(true);
    const image = captureImageFromVideo();
    if (image) {
      verifyImage(image)
        .then((res) => {
          setIsVerifying(false);
        })
        .catch((err) => {
          setIsVerifying(false);
        });
    } else {
      setIsVerifying(false);
    }
  };

  const paperClasses = isLandScapeMode ? landscapeModalStyles : modalStyles;

  return (
    <Modal
      action={{
        text: isVerifying
          ? t('ExternalCamera:FaceVerification.Verifying')
          : t('ExternalCamera:FaceVerification.Verify'),
        onSubmit: verifyFace,
        disabled: !isCameraReady || isVerifying,
      }}
      classes={{ paper: paperClasses }}
      header={{
        title: t('ExternalCamera:FaceVerification.VerifyIdentityHeading'),
        description: t('ExternalCamera:FaceVerification.VerifyIdentityDescription'),
        classes: { title: 'pl-16' },
      }}
      renderAs="dialog"
      renderHeader={modalHeaderRenderer}
      showCloseBtn={false}
      showCloseIcon={false}
      onClose={noop}>
      {isCameraReady ? (
        <Stack className="w-full" direction="col" gapY={16}>
          <Stack className="w-full" justifyContent="center">
            <Stack className="w-auto h-auto rounded-12 relative overflow-hidden">
              <div
                ref={(container) => {
                  const videoElement = videoRef.current;
                  if (container && videoElement && !container.contains(videoElement)) {
                    container.appendChild(videoElement);
                  }
                }}
                className={cn('w-[35rem] h-[24rem]', { 'rotate-90 transform': isLandScapeMode })}
              />
              <Stack className="absolute top-0 left-0 w-full h-full flex rounded-12 pointer-events-none">
                <div className="w-[25%] h-full bg-[rgba(0,0,0,0.2)] rounded-l-12"></div>
                <div className="w-[50%] h-full border-l border-r border-white"></div>
                <div className="w-[25%] h-full bg-[rgba(0,0,0,0.2)] rounded-r-12"></div>
              </Stack>
            </Stack>
          </Stack>
          <Stack>
            <Typography className="pl-24 pr-24" color="text-text-secondary" variant="body-short-02">
              {t('ExternalCamera:FaceVerification.VerificationInstruction')}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Stack alignItems="center" className="w-full h-[20rem]" justifyContent="center">
          <Loader />
          <Typography className="pl-8" color="text-text-secondary" variant="body-short-02">
            {t('ExternalCamera:FaceVerification.WaitingForCamera')}
          </Typography>
        </Stack>
      )}
    </Modal>
  );
}

export default FaceVerificationModal;
